/**
 * Empório Saibai — sincronização do contador do carrinho no header customizado.
 * Cria o badge quando o carrinho sai de zero e o remove quando volta a zero.
 * Build 2026-08-08.
 */
(() => {
  'use strict';
  if (window.__saibaiCartCountInit) return;
  window.__saibaiCartCountInit = true;

  const normalizeCount = (value) => {
    const count = Number.parseInt(value, 10);
    return Number.isFinite(count) ? Math.max(0, count) : null;
  };

  const countFromDetail = (detail) =>
    detail?.item_count ??
    detail?.data?.item_count ??
    detail?.cartData?.item_count ??
    detail?.data?.cartData?.item_count;

  const labelFor = (count) => {
    const template = count === 1
      ? window.cartStrings?.item
      : window.cartStrings?.items;
    return template
      ? template.replace('[count]', count)
      : `${count} ${count === 1 ? 'item' : 'itens'} no carrinho`;
  };

  const render = (value) => {
    const count = normalizeCount(value);
    if (count === null) return;

    const displayValue = count < 100
      ? String(count)
      : (window.cartStrings?.item_99 || '99+');

    document.querySelectorAll('.saibai-header__icon--cart').forEach((icon) => {
      let badge = icon.querySelector('[data-cart-count]');

      if (count === 0) {
        badge?.remove();
        return;
      }

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'saibai-header__cart-count';
        badge.setAttribute('data-cart-count', '');
        badge.setAttribute('aria-hidden', 'true');
        icon.appendChild(badge);
      }

      badge.textContent = displayValue;
    });

    const label = labelFor(count);
    document.querySelectorAll('.saibai-header__icon-btn--cart').forEach((control) => {
      control.setAttribute('aria-label', label);
    });
  };

  let cartSyncPromise = null;
  const syncFromCart = () => {
    if (cartSyncPromise) return cartSyncPromise;

    const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
    cartSyncPromise = fetch(cartUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`cart_count_${response.status}`);
        return response.json();
      })
      .then((cart) => render(cart.item_count))
      .catch(() => {})
      .finally(() => {
        cartSyncPromise = null;
      });

    return cartSyncPromise;
  };

  const handleCartEvent = (event) => {
    const count = countFromDetail(event?.detail);
    if (count === undefined || count === null) syncFromCart();
    else render(count);
  };

  const patchCartDrawer = async () => {
    if (!window.customElements?.whenDefined) return;

    try {
      await customElements.whenDefined('cart-drawer');
    } catch (_) {
      return;
    }

    const CartDrawerClass = customElements.get('cart-drawer');
    const prototype = CartDrawerClass?.prototype;
    if (!prototype || prototype.__saibaiCartCountPatched) return;

    const nativeUpdateCartCounts = prototype.updateCartCounts;
    prototype.updateCartCounts = function updateCartCountsWithSaibaiBadge(count) {
      const result = typeof nativeUpdateCartCounts === 'function'
        ? nativeUpdateCartCounts.call(this, count)
        : undefined;
      render(count);
      return result;
    };

    prototype.__saibaiCartCountPatched = true;
  };

  let pubSubBound = false;
  const bindPubSub = () => {
    if (pubSubBound) return;
    if (typeof subscribe !== 'function' ||
        typeof PUB_SUB_EVENTS === 'undefined' ||
        !PUB_SUB_EVENTS?.cartUpdate) return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = countFromDetail(event);
      if (count === undefined || count === null) syncFromCart();
      else render(count);
    });
    pubSubBound = true;
  };

  window.saibaiUpdateCartCount = render;
  window.saibaiSyncCartCount = syncFromCart;

  document.addEventListener('cart:updated', handleCartEvent);
  document.addEventListener('GiftWrapUpdateEvent', syncFromCart);
  document.addEventListener('shopify:section:load', syncFromCart);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncFromCart();
  });

  bindPubSub();
  patchCartDrawer();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindPubSub, { once: true });
  } else {
    bindPubSub();
  }
})();
